import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, TrendingUp, Globe, Shield, BookOpen, CreditCard, Award, Scale, Building2, Landmark, Wrench } from 'lucide-react';

const categories = ['All', 'Registration', 'Tax & Compliance', 'Finance', 'Legal', 'Digital'];

const services = [
  { title: 'Company Registration', desc: 'Pvt Ltd, LLP, OPC & more — fastest process', price: '₹1,499', category: 'Registration', icon: FileText, color: 'from-blue-500 to-cyan-500', popular: true },
  { title: 'GST Registration', desc: 'GSTIN in 3–5 working days', price: '₹999', category: 'Tax & Compliance', icon: Shield, color: 'from-purple-500 to-pink-500' },
  { title: 'Income Tax Filing', desc: 'ITR-1 to ITR-6 for businesses', price: '₹999', category: 'Tax & Compliance', icon: BookOpen, color: 'from-amber-500 to-orange-500' },
  { title: 'MSME Loans & Funding', desc: 'Up to ₹50L, quick disbursement', price: 'Free Apply', category: 'Finance', icon: Landmark, color: 'from-green-500 to-emerald-500', popular: true },
  { title: 'Trademark Registration', desc: 'Protect your brand identity', price: '₹4,999', category: 'Legal', icon: Award, color: 'from-rose-500 to-red-500' },
  { title: 'FSSAI Food License', desc: 'Mandatory for food businesses', price: '₹3,999', category: 'Registration', icon: FileText, color: 'from-lime-500 to-green-500' },
  { title: 'Digital Marketing', desc: 'SEO, Social Media & Google Ads', price: '₹4,999', category: 'Digital', icon: Globe, color: 'from-indigo-500 to-violet-500' },
  { title: 'MSME/Udyam Certificate', desc: 'Official MSME recognition', price: '₹499', category: 'Registration', icon: Building2, color: 'from-teal-500 to-cyan-500' },
  { title: 'Patent Registration', desc: 'Protect your innovations legally', price: '₹19,999', category: 'Legal', icon: Scale, color: 'from-violet-500 to-purple-500' },
  { title: 'ISO Certification', desc: 'ISO 9001/14001/22000/27001', price: '₹12,999', category: 'Tax & Compliance', icon: Shield, color: 'from-sky-500 to-blue-500' },
  { title: 'Virtual CFO Advisory', desc: 'Expert financial strategy for MSMEs', price: '₹19,999', category: 'Finance', icon: CreditCard, color: 'from-fuchsia-500 to-purple-500' },
  { title: 'Annual Compliance', desc: 'ROC filings & annual returns', price: '₹5,999', category: 'Tax & Compliance', icon: TrendingUp, color: 'from-sky-500 to-blue-500' },
];

export function Services() {
  const [activeCategory, setActiveCategory] = useState('All');
  const navigate = useNavigate();

  const filtered = activeCategory === 'All' ? services : services.filter(s => s.category === activeCategory);

  return (
    <section className="py-12 md:py-24 bg-white dark:bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-bold mb-6">
            <Wrench className="w-4 h-4" /> 35+ Professional Services
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Everything Your Business Needs
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
            From registration to funding — professional services tailored to your business, backed by certified CA/CS experts.
          </p>
        </motion.div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(cat => (
            <motion.button
              key={cat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                activeCategory === cat
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)]'
                  : 'bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm text-slate-700 dark:text-slate-300 hover:border-cyan-400/50'
              }`}
            >
              {cat}
            </motion.button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((service) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                className="relative group bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] rounded-2xl p-6 hover:border-cyan-400/50 dark:hover:border-cyan-400/40 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg dark:hover:shadow-2xl"
                onClick={() => navigate('/services')}
              >
                {service.popular && (
                  <span className="absolute -top-2.5 -right-2.5 px-3 py-1 rounded-full text-[10px] font-black bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow-lg uppercase tracking-wider">
                    Popular
                  </span>
                )}

                {/* Gradient top bar on hover */}
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className="w-6 h-6 text-white" />
                </div>

                <span className="text-[9px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-[#111C2F] px-2 py-0.5 rounded-full border border-slate-200 dark:border-[#2A3F5F]">
                  {service.category}
                </span>

                <h3 className="text-slate-900 dark:text-white font-bold text-base mt-3 mb-1.5 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{service.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-5">{service.desc}</p>

                <div className="flex items-center justify-end mt-auto border-t border-slate-100 dark:border-[#1E2A3A] pt-3">
                  <span className="text-cyan-700 dark:text-cyan-400 text-xs flex items-center gap-1 font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* View all */}
        <div className="text-center mt-12">
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/services')}
            className="px-8 py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-bold shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-500/30 transition-all"
          >
            View All 35+ Services →
          </motion.button>
        </div>
      </div>
    </section>
  );
}
